<template>
  <div class="prelabel-workspace" :class="{ 'prelabel-workspace--config-only': !textTask }">
    <aside class="prelabel-config-panel"><slot></slot></aside>
    <section v-show="textTask" class="prelabel-input-panel">
      <div class="prelabel-panel-heading">
        <h3>输入待标注文本</h3>
      </div>
      <div class="prelabel-text-field">
        <el-input ref="textInput" :value="displayText" class="prelabel-text-input" type="textarea" resize="none"
          label="输入待标注文本" placeholder="粘贴待标注文本，或填入示例文本快速体验"
          @beforeinput.native="onBeforeTextInput" @input="onTextInput" @paste.native="onTextPaste" @keydown.native="onTextKeydown"
          @compositionstart.native="onCompositionStart" @compositionend.native="onCompositionEnd"></el-input>
        <span class="prelabel-word-limit">{{ textLength }} / 2000</span>
      </div>
      <div class="prelabel-input-actions">
        <el-button type="text" size="small" @click="commitText(exampleText)">填入示例文本</el-button>
        <el-button class="prelabel-clear-button" size="mini" @click="commitText('')">清空</el-button>
        <el-button type="primary" size="mini" class="prelabel-run-button" :loading="status === 'loading'"
          :disabled="!!unavailableReason || status === 'loading'" @click="runPreview">预标注效果</el-button>
      </div>
      <p v-if="unavailableReason" class="prelabel-help">{{ unavailableReason }}</p>
    </section>
    <section v-show="textTask" class="prelabel-result-panel">
      <div class="prelabel-panel-heading"><h3>预标注结果</h3></div>
      <div class="prelabel-result-content">
        <micro-app v-if="active && entryStatus === 'ready' && appName" :key="appName" v-show="showResult" class="prelabel-micro-app"
          :name="appName" :url="previewUrl" :iframe="true" destroy clear-data
          :default-page="previewPath" :data="previewMessage"
          @mounted="sendPreview" @datachange="onPreviewMessage" @error="onEmbedError"></micro-app>
        <div v-if="!showResult || !rendered" class="prelabel-empty-state" :class="{ 'prelabel-load-error': entryStatus === 'error' || embedError }">
        <i v-if="entryStatus !== 'error' && !embedError" class="el-icon-document"></i>
        <strong>{{ resultTitle }}</strong>
        <p role="status">{{ entryError || embedError || message || (entryStatus === 'loading' ? '正在获取当前环境的预览入口，请稍候' : '输入待标注文本后点击“预标注效果”，查看模型与当前标签的匹配情况。') }}</p>
        <el-button v-if="entryStatus === 'error' || embedError" type="primary" plain size="small" @click="retryLoading">重试加载</el-button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { countTaskLabels } from '@/uilt/labelTaskCapacity';
import { isTextPreviewModality, requestPrelabelPreview, requestPrelabelPreviewEntry } from '@/uilt/prelabelPreviewApi';
import microApp from '@micro-zoe/micro-app';

const loadFailureMessage = '预览工具加载失败，可重试加载；当前输入、配置及有效结果已保留。';

export default {
  name: 'LabelPrelabelPreview',
  props: {
    active: Boolean,
    textTask: Boolean,
    modelGuid: { type: String, default: '' },
    model: { type: Object, default: null },
    labelSnapshots: { type: Array, default: () => [] },
    labelsLoading: Boolean,
    selectionRevision: { type: Number, default: 0 },
  },
  data() {
    return {
      text: '',
      exampleText: '主诉：发热、咳嗽4天，气促1天',
      sessionId: '', appName: '', previewUrl: '', revision: 0,
      entryStatus: 'idle', entryError: '',
      status: 'idle', message: '', embedError: '', rendered: false, result: null, attempted: false,
    };
  },
  computed: {
    textLength() { return Array.from(this.text).length; },
    displayText() { return this.text.replace(/\r\n?/g, '\n'); },
    previewPath() { return this.previewUrl ? new URL(this.previewUrl).pathname : ''; },
    unavailableReason() {
      if (this.entryStatus === 'error') return '预览入口加载失败，可重试加载；仍可确认当前配置';
      if (this.entryStatus !== 'ready') return '预览入口加载中，请稍候';
      if (this.embedError) return '预览工具加载失败，请先重试加载';
      if (this.labelsLoading) return '标签详情加载中，请稍候';
      if (!this.modelGuid || !this.model) return '请选择可用的文本预标注模型';
      if (this.model.enableState && this.model.enableState !== 'Y') return '所选模型已停用，请选择可用模型';
      if (!isTextPreviewModality(this.model.dataModalGuid, this.model.dataModalName)) return '所选模型不支持文本预览';
      if (!countTaskLabels(this.labelSnapshots)) return '请选择有效的任务标签';
      if (!this.text.trim()) return '请输入待标注文本';
      if (this.textLength > 2000) return '文本不能超过 2000 字';
      return '';
    },
    showResult() { return ['success', 'empty'].includes(this.status) && !this.embedError; },
    resultTitle() {
      if (this.entryStatus === 'error') return '预览入口加载失败';
      if (this.entryStatus === 'loading') return '正在加载预览入口';
      if (this.embedError) return '预览工具加载异常';
      if (this.status === 'error') return '预标注失败';
      return { idle: '尚未生成预标注结果', loading: '正在生成预标注结果，最多等待 60 秒',
        stale: '输入条件已变化，请重新预览', error: '预标注失败',
        success: '正在展示预标注结果', empty: '未识别到实体' }[this.status];
    },
    previewMessage() {
      return {
        type: 'PRELABEL_PREVIEW', protocolVersion: 1, sessionId: this.sessionId, revision: this.revision,
        status: this.status, text: this.result ? this.result.text : this.text,
        labelConfig: this.result ? this.result.labelConfig : '',
        predictions: this.result ? this.result.predictions : [],
        serviceModelVersion: this.result ? this.result.serviceModelVersion : '', message: this.message,
      };
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(value) { if (value) this.openPreview(); else this.closePreview(); },
    },
    textTask(value) {
      if (!this.active) return;
      if (value) this.openPreview();
      else this.invalidatePreview();
    },
    text() { this.invalidatePreview(); },
    modelGuid() { this.invalidatePreview(); },
    labelSnapshots: { deep: true, handler() { this.invalidatePreview(); } },
    selectionRevision() { this.invalidatePreview(); },
    previewMessage() { this.sendPreview(); },
  },
  beforeDestroy() { this.closePreview(); },
  methods: {
    openPreview() {
      if (!this.active || !this.textTask) return;
      if (this.sessionId) {
        if (this.entryStatus === 'ready' && !this.appName) this.mountPreview();
        return;
      }
      this.sessionId = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
      this.status = 'idle';
      this.loadPreviewEntry();
    },
    loadPreviewEntry() {
      if (this._entryRequest) this._entryRequest.cancel();
      const sequence = this._entrySequence = (this._entrySequence || 0) + 1;
      this.entryStatus = 'loading';
      this.entryError = '';
      this.previewUrl = '';
      const session = this.sessionId;
      const current = () => this.active && this.sessionId === session && sequence === this._entrySequence;
      const request = requestPrelabelPreviewEntry();
      this._entryRequest = request;
      request.promise.then(url => {
        if (!current()) return;
        this.previewUrl = url;
        this.entryStatus = 'ready';
        if (this.textTask) this.mountPreview();
      }).catch(error => {
        if (!current()) return;
        this.entryStatus = 'error';
        this.entryError = (error.message || '预览入口加载失败，请重试') + '；仍可确认当前配置。';
      }).finally(() => {
        if (current()) this._entryRequest = null;
      });
    },
    // 仅明确的加载故障允许重建；正常配置、布局变化继续复用本次实例。
    retryLoading() {
      if (!this.active || !this.textTask) return;
      if (this.entryStatus === 'error') this.loadPreviewEntry();
      else if (this.entryStatus === 'ready' && this.embedError) this.mountPreview();
    },
    mountPreview() {
      const previousName = this.appName;
      const session = this.sessionId;
      const generation = this._embedGeneration = (this._embedGeneration || 0) + 1;
      this.appName = '';
      this.embedError = '';
      this.rendered = false;
      this.$nextTick(() => {
        if (previousName) microApp.clearData(previousName);
        if (!this.active || !this.textTask || this.sessionId !== session || this.entryStatus !== 'ready'
          || generation !== this._embedGeneration) return;
        this.appName = 'label-studio-preview-' + session + '-' + generation;
      });
    },
    stopRequest() {
      if (this._previewRequest) this._previewRequest.cancel();
      this._previewRequest = null;
      clearTimeout(this._previewTimer);
      this._previewTimer = null;
    },
    closePreview() {
      this.stopRequest();
      if (this._entryRequest) this._entryRequest.cancel();
      this._entryRequest = null;
      this._entrySequence = (this._entrySequence || 0) + 1;
      this._embedGeneration = (this._embedGeneration || 0) + 1;
      const name = this.appName;
      this.appName = '';
      this.sessionId = '';
      this.previewUrl = '';
      this.entryStatus = 'idle';
      this.entryError = '';
      this.revision += 1;
      this.text = '';
      this.result = null;
      this.status = 'idle';
      this.message = '';
      this.embedError = '';
      this.rendered = false;
      this.attempted = false;
      this._undoText = [];
      this._redoText = [];
      this._pendingTextEdit = null;
      this._compositionState = null;
      if (name) {
        // Vue 负责移除自身的 DOM；销毁由元素属性触发，避免手动移除与 Vue 更新竞争。
        this.$nextTick(() => microApp.clearData(name));
      }
    },
    invalidatePreview() {
      if (!this.active) return;
      this.stopRequest();
      this.revision += 1;
      this.result = null;
      this.rendered = false;
      this.message = '';
      this.status = this.attempted ? 'stale' : 'idle';
    },
    sendPreview() {
      if (this.active && this.entryStatus === 'ready' && this.appName && this.previewUrl) {
        microApp.setData(this.appName, this.previewMessage, undefined, true);
      }
    },
    onPreviewMessage(event) {
      const data = event.detail && event.detail.data;
      if (!this.active || !data || data.protocolVersion !== 1) return;
      if (event.currentTarget && event.currentTarget.getAttribute('name') !== this.appName) return;
      if (data.type === 'PRELABEL_PREVIEW_READY') {
        this.embedError = '';
        this.sendPreview();
        return;
      }
      if (data.sessionId !== this.sessionId || data.revision !== this.revision) return;
      if (data.type === 'PRELABEL_PREVIEW_RENDERED') this.rendered = true;
      if (data.type === 'PRELABEL_PREVIEW_ERROR') {
        if (data.errorCode === 'load_error') {
          this.rendered = false;
          this.embedError = loadFailureMessage;
          return;
        }
        this.result = null;
        this.rendered = false;
        this.embedError = '';
        this.status = 'error';
        this.message = typeof data.message === 'string' ? data.message : '预测结果无法展示，请重试';
      }
    },
    onEmbedError(event) {
      if (!this.active || !event.currentTarget || event.currentTarget.getAttribute('name') !== this.appName) return;
      this.embedError = loadFailureMessage;
    },
    runPreview() {
      if (this.unavailableReason || this.status === 'loading' || !this.active || !this.textTask) return;
      this.stopRequest();
      const session = this.sessionId;
      const revision = ++this.revision;
      const text = this.text;
      this.attempted = true;
      this.status = 'loading';
      this.result = null;
      this.rendered = false;
      this.message = '';
      const current = () => this.active && this.sessionId === session && this.revision === revision;
      this._previewTimer = setTimeout(() => {
        if (!current()) return;
        this.stopRequest();
        this.revision += 1;
        this.status = 'error';
        this.message = '预标注超时（最多等待 60 秒），请重试';
      }, 60000);
      const request = requestPrelabelPreview({
        tenantGuid: localStorage.getItem('guid') || '', modelGuid: this.modelGuid, text,
        labelConfig: JSON.stringify({ labelSnapshots: this.labelSnapshots.filter(Boolean) }),
      });
      this._previewRequest = request;
      request.promise.then(result => {
        if (!current()) return;
        if (!result || result.text !== text || typeof result.labelConfig !== 'string' || !result.labelConfig
          || !Array.isArray(result.predictions) || !result.predictions.length
          || result.predictions.some(prediction => !prediction || !Array.isArray(prediction.result))) {
          throw new Error('预览接口返回的原文或预测结构异常，请重试');
        }
        this.result = result;
        this.status = result.predictions.some(prediction => prediction.result.length) ? 'success' : 'empty';
      }).catch(error => {
        if (!current()) return;
        this.status = 'error';
        this.message = error.message || '预标注失败，请重试';
      }).finally(() => {
        if (current()) {
          clearTimeout(this._previewTimer);
          this._previewTimer = null;
          this._previewRequest = null;
        }
      });
    },
    editorState(input = this.$refs.textInput && this.$refs.textInput.getInput()) {
      return { text: this.text, start: input ? input.selectionStart : 0, end: input ? input.selectionEnd : 0 };
    },
    rememberText(state) {
      this._undoText = [...(this._undoText || []), state].slice(-100);
      this._redoText = [];
    },
    commitText(value, previous) {
      const next = Array.from(value);
      if (next.length > 2000) {
        const current = Array.from(this.text);
        let start = 0;
        while (start < current.length && start < next.length && current[start] === next[start]) start += 1;
        let suffix = 0;
        while (suffix < current.length - start && suffix < next.length - start
          && current[current.length - 1 - suffix] === next[next.length - 1 - suffix]) suffix += 1;
        const available = Math.max(0, 2000 - start - suffix);
        value = next.slice(0, start + available).concat(suffix ? next.slice(-suffix) : []).join('');
      }
      if (value === this.text) return;
      if (!this._compositionState) this.rememberText(previous || this.editorState());
      this.text = value;
    },
    restoreText(redo, input) {
      const source = redo ? this._redoText : this._undoText;
      if (!source || !source.length) return;
      const state = source.pop();
      const destination = redo ? (this._undoText || (this._undoText = [])) : (this._redoText || (this._redoText = []));
      destination.push(this.editorState(input));
      this._pendingTextEdit = null;
      this.text = state.text;
      this.$nextTick(() => input.setSelectionRange(state.start, state.end));
    },
    onTextKeydown(event) {
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.isComposing || this._compositionState) return;
      const key = event.key.toLowerCase();
      if (key !== 'z' && key !== 'y') return;
      event.preventDefault();
      this.restoreText(key === 'y' || event.shiftKey, event.target);
    },
    onCompositionStart(event) { this._compositionState = this.editorState(event.target); },
    onCompositionEnd() {
      const previous = this._compositionState;
      this.$nextTick(() => {
        if (this._compositionState !== previous) return;
        this._compositionState = null;
        if (previous && previous.text !== this.text) this.rememberText(previous);
      });
    },
    onBeforeTextInput(event) {
      if (!this._compositionState && ['historyUndo', 'historyRedo'].includes(event.inputType)) {
        event.preventDefault();
        this.restoreText(event.inputType === 'historyRedo', event.target);
        return;
      }
      this._pendingTextEdit = { ...this.editorState(event.target), type: event.inputType };
    },
    // 选区以 textarea 显示位置计数；映射回原文时，CRLF 仍是两个码点。
    rawOffset(offset) {
      let raw = 0;
      for (let shown = 0; shown < offset && raw < this.text.length; shown += 1) {
        raw += this.text[raw] === '\r' && this.text[raw + 1] === '\n' ? 2 : 1;
      }
      return raw;
    },
    onTextInput() {
      const input = this.$refs.textInput && this.$refs.textInput.getInput();
      if (!input) return;
      const before = this.displayText;
      const after = input.value;
      // Element UI 在组合输入结束后才派发 input，期间的选区已变化，按实际文本差异还原原始换行。
      const edit = this._compositionState ? null : this._pendingTextEdit;
      this._pendingTextEdit = null;
      if (edit && edit.text === this.text) {
        let { start, end } = edit;
        const removed = before.length - after.length;
        if (start === end && removed >= 0 && (edit.type || '').startsWith('delete')) {
          if (edit.type.endsWith('Backward')) start = Math.max(0, start - removed);
          else end = Math.min(before.length, end + removed);
        }
        const inserted = after.length - before.length + end - start;
        if (inserted >= 0 && before.slice(0, start) === after.slice(0, start)
          && before.slice(end) === after.slice(start + inserted)) {
          this.commitText(this.text.slice(0, this.rawOffset(start)) + after.slice(start, start + inserted)
            + this.text.slice(this.rawOffset(end)), edit);
          return;
        }
      }
      // 组合输入或浏览器未提供 beforeinput 时，仅更新实际变化片段。
      let start = 0;
      while (start < before.length && start < after.length && before[start] === after[start]) start += 1;
      let suffix = 0;
      while (suffix < before.length - start && suffix < after.length - start
        && before[before.length - 1 - suffix] === after[after.length - 1 - suffix]) suffix += 1;
      this.commitText(this.text.slice(0, this.rawOffset(start)) + after.slice(start, after.length - suffix)
        + this.text.slice(this.rawOffset(before.length - suffix)), edit || this.editorState(input));
    },
    onTextPaste(event) {
      if (!event.clipboardData) return;
      event.preventDefault();
      const input = event.target;
      const before = this.text.slice(0, this.rawOffset(input.selectionStart));
      const after = this.text.slice(this.rawOffset(input.selectionEnd));
      const available = Math.max(0, 2000 - Array.from(before + after).length);
      const pasted = Array.from(event.clipboardData.getData('text/plain')).slice(0, available).join('');
      const cursor = input.selectionStart + pasted.replace(/\r\n?/g, '\n').length;
      this._pendingTextEdit = null;
      this.commitText(before + pasted + after, this.editorState(input));
      this.$nextTick(() => input.setSelectionRange(cursor, cursor));
    },
  },
};
</script>

<style scoped>
.prelabel-workspace { display: grid; grid-template-columns: 290px 330px minmax(0, 1fr); gap: 14px; flex: 1; min-height: 0; text-align: left; }
.prelabel-config-panel, .prelabel-input-panel, .prelabel-result-panel { min-width: 0; min-height: 0; border: 1px solid #e4e7ed; border-radius: 6px; background: #fff; }
.prelabel-config-panel { padding: 16px; overflow: auto; }
.prelabel-input-panel { padding: 16px; display: flex; flex-direction: column; }
.prelabel-result-panel { display: flex; flex-direction: column; overflow: hidden; }
.prelabel-result-content { flex: 1; min-height: 0; position: relative; }
.prelabel-micro-app { display: block; width: 100%; height: 100%; }
.prelabel-panel-heading { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-shrink: 0; }
.prelabel-panel-heading h3 { font-size: 14px; margin: 0 0 12px; color: #303133; }
.prelabel-result-panel > .prelabel-panel-heading { padding: 16px 16px 0; }
.prelabel-text-field { flex: 1; min-height: 150px; position: relative; }
.prelabel-text-input { width: 100%; height: 100%; }
.prelabel-text-input /deep/ .el-textarea__inner { height: 100%; min-height: 150px; padding: 12px 12px 30px; box-sizing: border-box; background: #fafbfc; font: inherit; line-height: 1.8; }
.prelabel-word-limit { position: absolute; right: 10px; bottom: 6px; padding-left: 4px; color: #909399; background: #fafbfc; font-size: 12px; line-height: 1.5; }
.prelabel-input-actions { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: center; gap: 8px; margin: 8px 0 0; }
.prelabel-input-actions .el-button + .el-button { margin-left: 0; }
.prelabel-input-actions > .el-button--text { justify-self: start; }
.prelabel-run-button { grid-column: 1 / 3; width: 100%; padding: 12px 20px; font-size: 14px; }
.prelabel-help { margin: 8px 0 0; font-size: 12px; line-height: 1.6; }
.prelabel-help { color: #f56c6c; }
.prelabel-empty-state { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; flex-direction: column; padding: 24px; color: #909399; background: #fff; text-align: center; }
.prelabel-empty-state i { font-size: 34px; margin-bottom: 14px; color: #b8c5cd; }
.prelabel-empty-state strong { color: #606266; }
.prelabel-empty-state p { max-width: 420px; line-height: 1.8; font-size: 13px; }
.prelabel-load-error { padding: 12px; }
.prelabel-load-error p { margin: 8px 0; }
.prelabel-workspace--config-only { grid-template-columns: minmax(0, 1fr); }
@media (max-width: 1500px) {
  .prelabel-workspace:not(.prelabel-workspace--config-only) { grid-template-columns: 290px minmax(0, 1fr); grid-template-rows: 160px minmax(0, 1fr); }
  .prelabel-config-panel { grid-row: 1 / 3; }
  .prelabel-input-panel { grid-column: 2; display: grid; grid-template-columns: minmax(0, 1fr) auto; grid-template-rows: auto minmax(0, 1fr) auto; gap: 8px 12px; }
  .prelabel-input-panel .prelabel-panel-heading { grid-column: 1; grid-row: 1; }
  .prelabel-input-panel .prelabel-panel-heading h3 { margin: 0; }
  .prelabel-text-field { grid-column: 1 / 3; grid-row: 2; min-height: 0; height: 100%; }
  .prelabel-text-input /deep/ .el-textarea__inner { min-height: 0; padding: 8px 10px 24px; line-height: 1.5; }
  .prelabel-input-actions { grid-column: 2; grid-row: 1; display: flex; gap: 8px; margin: 0; }
  .prelabel-clear-button, .prelabel-run-button { padding: 7px 8px; font-size: 12px; }
  .prelabel-run-button { order: -1; width: 108px; }
  .prelabel-clear-button { width: 84px; }
  .prelabel-input-actions > .el-button--text { order: 1; }
  .prelabel-help { grid-column: 1 / 3; margin: 0; line-height: 1.3; }
  .prelabel-result-panel { grid-column: 2; }
}
@media (max-width: 1500px) and (max-height: 700px) {
  .prelabel-workspace:not(.prelabel-workspace--config-only) { grid-template-rows: 140px minmax(0, 1fr); }
}
</style>
